<template>
  <section class="contacts-app">
    <header class="contacts-header">
      <h3>AI Contact Intelligence Research</h3>

      <div class="row">
        <input
          ref="queryInput"
          v-model.trim="query"
          :placeholder="placeholderText"
          @keyup.enter="runPipeline"
        />
        <select v-model="targetType" class="select">
          <option value="person">Person</option>
          <option value="artist">Artist</option>
        </select>
        <button class="btn" :disabled="!query || loading" @click="runPipeline">
          {{ loading ? 'Analyzing…' : 'Search & Analyze' }}
        </button>
      </div>

      <small class="muted">
        Runs GoogleCSE + AIReporter to collect web data, then uses OpenAI (client‑side) to synthesize verified contact profiles.
      </small>
    </header>

    <div v-if="error" class="error">⚠️ {{ error }}</div>

    <!-- FRIENDLY EMPTY-STATE -->
    <div v-if="!loading && collected.length === 0 && !contacts && query" class="hint">
      🤖 No research sources were found for "<b>{{ query }}</b>". Try refining the name or add keywords like
      <em>"official site"</em>, <em>"management"</em>, <em>"booking"</em>, <em>"contact"</em>.
    </div>

    <!-- SOURCE TOOLS -->
    <section class="source-tools">
      <div class="row">
        <button class="btn ghost" @click="runAiReporter">🔎 Run Google (AiReporter)</button>
        <button class="btn ghost" :disabled="!collected.length || loading" @click="onSynthesize">
          🧠 Synthesize Contacts
        </button>
        <button class="btn" :disabled="!collected.length" @click="saveSources">💾 Save Sources</button>
      </div>

      <div class="google-cse-wrapper">
        <div class="gcse-searchbox" id="cse-search"></div>
        <div class="gcse-searchresults" id="cse-results"></div>
      </div>

      <AiReporter
        ref="reporter"
        @report-finished="onReporterFinished"
        @sources-collected="onReporterSources"
        @results="onReporterSources"
      />

      <div v-if="collected.length" class="results">
        <h4>Collected Results ({{ collected.length }})</h4>
        <ul>
          <li v-for="(r, i) in collected.slice(0, 12)" :key="'src-'+i">
            <a :href="r.link" target="_blank" rel="noopener">{{ r.title }}</a>
            <p class="snippet">{{ r.snippet }}</p>
          </li>
        </ul>
      </div>
    </section>

    <!-- FILTERS -->
    <div v-if="contacts?.contacts?.length" class="filter-bar">
      <input v-model="filterQuery" placeholder="Filter by name, role, company, or email…" />
      <div class="chipbar">
        <button class="chip" :class="{ active: sortKey === 'confidence' }" @click="sortKey = 'confidence'">
          Sort: Confidence
        </button>
        <button class="chip" :class="{ active: sortKey === 'role' }" @click="sortKey = 'role'">
          Sort: Role
        </button>
        <button class="chip" :class="{ active: sortKey === 'name' }" @click="sortKey = 'name'">
          Sort: Name
        </button>
      </div>
    </div>

    <!-- SYNTHESIZED SUMMARY -->
    <section v-if="contacts" class="synth">
      <header class="synth-head">
        <div>
          <h4>{{ contacts.primaryName || contacts.query }}</h4>
          <div class="muted">Confidence: {{ (contacts.confidence || 0).toFixed(2) }}</div>
        </div>
        <div class="row">
          <button class="btn ghost" @click="downloadJSON">⬇️ JSON</button>
          <button class="btn ghost" @click="downloadVCF">📇 vCard</button>
          <button class="btn" @click="saveContacts">💾 Save Contacts</button>
        </div>
      </header>

      <p v-if="contacts.summary" class="summary">{{ contacts.summary }}</p>

      <div v-if="contacts.suggestions?.length" class="suggestions">
        <h5>AI Suggestions</h5>
        <ul>
          <li v-for="(s, i) in contacts.suggestions" :key="'sug-'+i">💡 {{ s }}</li>
        </ul>
      </div>

      <div v-if="contacts.profiles?.length" class="profiles">
        <h5>Profiles</h5>
        <ul>
          <li v-for="p in contacts.profiles" :key="p.url">
            <a :href="p.url" target="_blank" rel="noopener">{{ p.title || p.url }}</a>
            <small class="muted">— {{ p.type }}</small>
          </li>
        </ul>
      </div>
    </section>
<section v-if="contacts && contacts.aiReport" class="ai-report">
  <h4>📊 AI Tour/Contact Report</h4>
  <pre class="report-block">{{ contacts.aiReport }}</pre>
</section>

    <!-- CONTACT CARDS -->
    <transition-group name="fade-slide" tag="div" class="contacts-grid" v-if="contacts?.contacts?.length">
      <article
        v-for="(c, idx) in filteredAndSortedContacts"
        :key="c.id || idx"
        class="card"
        :class="{ verified: c.verified }"
      >
        <header>
          <strong>{{ c.role || 'Contact' }}</strong>
          <span v-if="c.verified" class="badge verified">✔ Verified</span>
          <span class="conf">{{ (c.confidence || 0).toFixed(2) }}</span>
        </header>

        <div v-if="c.name">👤 <b>{{ c.name }}</b></div>
        <div v-if="c.company">🏢 {{ c.company }}</div>
        <div v-if="c.region">📍 {{ c.region }}</div>

        <div v-if="c.emails?.length">
          <div class="label">📧 Emails</div>
          <ul class="tiny">
            <li v-for="(e, j) in c.emails" :key="'e-'+j">
              <code>{{ e.email }}</code>
              <small v-if="e.confidence !== undefined" class="muted">({{ e.confidence.toFixed(2) }})</small>
              <div class="sources" v-if="e.sources?.length">
                <a v-for="(u,k) in e.sources" :key="'es-'+k" :href="u" target="_blank" rel="noopener">source {{ k+1 }}</a>
              </div>
            </li>
          </ul>
        </div>

        <div v-if="c.phones?.length">
          <div class="label">📞 Phones</div>
          <ul class="tiny">
            <li v-for="(p, j) in c.phones" :key="'p-'+j">{{ p.phone }}</li>
          </ul>
        </div>

        <div v-if="c.socials?.length">
          <div class="label">🌐 Socials</div>
          <ul class="tiny">
            <li v-for="(s, j) in c.socials" :key="'s-'+j">
              <a :href="s.url" target="_blank" rel="noopener">{{ s.platform }}: {{ s.handle || s.url }}</a>
            </li>
          </ul>
        </div>

        <div v-if="c.suggestedAction" class="muted ai-suggestion">💬 {{ c.suggestedAction }}</div>

        <!-- Outreach draft (AI) -->
        <div class="outreach">
          <button class="btn ghost" @click="composeOutreach(idx, c)">✉️ Compose Outreach</button>
          <div v-if="outreachDrafts[idx]" class="outreach-draft">
            <h6>Draft</h6>
            <textarea :value="outreachDrafts[idx]" readonly></textarea>
            <div class="row">
              <button class="btn ghost" @click="copyDraft(idx)">Copy</button>
              <button class="btn" @click="saveDraft(idx, c)">Save to Archive</button>
            </div>
          </div>
        </div>
      </article>
    </transition-group>
  </section>
</template>

<script>
import AiReporter from './AIReporter.vue';
import { AI } from '../utils/ai';
import { ArchiveAPI, NeuralAPI } from '../utils/storage';

export default {
  name: 'ResearchContacts',
  components: { AiReporter },
  data() {
    return {
      query: '',
      targetType: 'person',     // person | artist
      loading: false,
      error: '',
      collected: [],            // [{title, link, snippet}]
      contacts: null,           // AI synthesized contacts report
      filterQuery: '',
      sortKey: 'confidence',    // 'confidence' | 'role' | 'name'
      outreachDrafts: {},       // { [idx]: string }
      currentConv: null,

      // CSE state
    cseLoaded: false,
    cseReady: false,
    resultObserver: null,
    reporterHooked: false,
    };
  },
  computed: {
    placeholderText() {
      return 'Search for a person or artist (e.g., "Taylor Swift" or "Elon Musk")';
    },
    filteredAndSortedContacts() {
      const list = (this.contacts?.contacts || []).filter(c => {
        const q = this.filterQuery.trim().toLowerCase();
        if (!q) return true;
        return (
          (c.name && c.name.toLowerCase().includes(q)) ||
          (c.role && c.role.toLowerCase().includes(q)) ||
          (c.company && c.company.toLowerCase().includes(q)) ||
          (c.region && c.region.toLowerCase().includes(q)) ||
          (c.emails || []).some(e => (e.email || '').toLowerCase().includes(q))
        );
      });

      const by = this.sortKey;
      return list.sort((a, b) => {
        if (by === 'confidence') return (b.confidence || 0) - (a.confidence || 0);
        if (by === 'role') return String(a.role || '').localeCompare(String(b.role || ''));
        if (by === 'name') return String(a.name || '').localeCompare(String(b.name || ''));
        return 0;
      });
    }
  },
  mounted() {
    this.$nextTick(() => this.$refs.queryInput && this.$refs.queryInput.focus());
    this.loadGoogleCSE();
    this.armReporterHooks();
  },
  beforeDestroy() {
    if (this.__resultObserver && this.__resultObserver.disconnect) {
      this.__resultObserver.disconnect();
    }
    const r = this.$refs && this.$refs.reporter;
    if (r && r.$off) {
      r.$off('report-finished', this.onReporterFinished);
      r.$off('sources-collected', this.onReporterSources);
      r.$off('results', this.onReporterSources);
    }
  },
  methods: {
    // ========== MAIN PIPELINE ==========
    async runPipeline() {
      this.error = '';
      this.contacts = null;
      this.collected = [];
      if (!this.query) return;

      try {
        this.loading = true;

        // 1) Run reporter and wait for sources
        await this.runAiReporter();
        await this.waitForSources(6000);

        // 2) Fallback to CSE scraping if nothing yet
        if (!this.collected.length) {
          await this.triggerCseSearch(this.query);
          await this.wait(1200);
          this.collectGoogleResults();
        }

        if (!this.collected.length) {
          this.error = `No sources were collected for "${this.query}". Refine your search (e.g., add "official site", "management", "booking").`;
          return;
        }

        // 3) Synthesize with client-side OpenAI
        await this.onSynthesize();
      } catch (e) {
        this.error = e?.message || String(e);
      } finally {
        this.loading = false;
      }
    },

    // ========== AIReporter ==========
    async runAiReporter() {
      const reporter = this.$refs && this.$refs.reporter;
      if (reporter && reporter.runReporter) {
        this.armReporterHooks();
        try {
          await reporter.runReporter(this.query, { forceOpen: true, speak: false });
          // Also schedule a backup scrape
          setTimeout(() => this.collectGoogleResults(), 1800);
        } catch (e) {
          // Reporter failed—fall back silently
          console.warn('[AiReporter] runReporter failed:', e);
        }
      } else {
        // Reporter not available—try CSE
        await this.triggerCseSearch(this.query);
      }
    },

    waitForSources(timeout = 5000) {
      return new Promise(resolve => {
        const start = Date.now();
        const tick = () => {
          if (this.collected.length > 0 || Date.now() - start > timeout) return resolve();
          setTimeout(tick, 100);
        };
        tick();
      });
    },

    armReporterHooks() {
      if (this.__reporterHooked) return;
      const r = this.$refs && this.$refs.reporter;
      if (!r || !r.$on) return;
      r.$on('report-finished', this.onReporterFinished);
      r.$on('sources-collected', this.onReporterSources);
      r.$on('results', this.onReporterSources);
      this.__reporterHooked = true;
    },

    onReporterSources(evt) {
      const normalized = this.normalizeReporterResults(evt);
      if (normalized.length) {
        this.mergeAndDedupeSources(normalized);
      }
    },

    onReporterFinished(payload) {
      const normalized = this.normalizeReporterResults(payload);
      if (normalized.length) {
        this.mergeAndDedupeSources(normalized);
      }
    },

    normalizeReporterResults(payload) {
      const out = [];
      if (!payload) return out;

      const pushItem = (r) => {
        const title = String(r.title || r.name || '').trim();
        const link  = String(r.link || r.url || '').trim();
        const snippet = String(r.snippet || r.summary || r.finding || r.quote || '').trim();
        if (title && link) out.push({ title, link, snippet });
      };

      if (Array.isArray(payload)) {
        payload.forEach(pushItem);
      } else if (payload && typeof payload === 'object') {
        []
          .concat(payload.results || [])
          .concat(payload.sources || [])
          .concat(payload.steps || [])
          .forEach(pushItem);
      }
      return out;
    },

    mergeAndDedupeSources(arr) {
      const all = [...this.collected, ...arr];
      const seen = new Set();
      const deduped = [];
      for (const r of all) {
        const key = this.normalizeUrl(r.link);
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(r);
      }
      this.collected = deduped.slice(0, 50);
    },

    normalizeUrl(u = '') {
      try {
        const url = new URL(u);
        url.hash = '';
        return url.toString();
      } catch (e) {
        return u;
      }
    },

    // ========== SYNTHESIS (client-side OpenAI) ==========
    async onSynthesize() {
      const result = await this.synthesizeContactsWithAI();
      if (!result) {
        throw new Error('Synthesis failed. Try adjusting your search terms.');
      }
      this.contacts = result;
      this.persistContactsToBrain('client-ai');
    },

async synthesizeContactsWithAI() {
  if (!this.collected.length) return null;

  // Build a combined snippet text from collected results
  const sources = this.collected.slice(0, 20).map((r, i) =>
    `${i + 1}. ${r.title}\nURL: ${r.link}\nSnippet: ${r.snippet}`
  ).join('\n\n');
  console.log('Collected Sources for Prompt:', sources);

  const result = await fetch('https://api.openai.com/v1/responses', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${process.env.OPENAI_API_KEY || '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'}`
    },
    body: JSON.stringify({
      prompt: {
        id: "pmpt_68e240673a3081939fafe79162746366058da9d73aedf129",
        version: "3",
        variables: {
          years_back: "5", // you can make this user-editable
          artist_name: this.query,
          regions: "North America, Europe",
          goals: "Find management, booking, PR contacts",
          deliverables: "Contact report with JSON + suggestions",
          automation: "Manual review",
          share_format: "JSON",
          top_n: "10",
          artist_list: "",
          capacity_range: "500-20000",
          num_stops: "10"
        }
      }
    })
  });

  const data = await result.json();

  // Extract and validate the text or JSON response
  const text = data?.choices?.[0]?.text || data?.text || '';
  const parsed = this.tryParseJson(text);
  if (!parsed) return null;

  (parsed.contacts || []).forEach((c, i) => {
    c.id = c.id || this.slugify(`${c.role || 'contact'}-${c.name || i}-${c.company || ''}`);
  });

  parsed.primaryName = parsed.primaryName || this.query;
  parsed.query = this.query;
  return parsed;
},


    // ========== OUTREACH ==========
    async composeOutreach(idx, contact) {
      try {
        const who = (this.contacts && this.contacts.primaryName) || this.query;
        const role = contact.role || 'Contact';
        const name = contact.name || '';
        const company = contact.company || '';
        const email = (contact.emails && contact.emails[0] && contact.emails[0].email) || '';

        const prompt = `
You're an assistant. Draft a concise, warm outreach email to ${role} ${name} ${company ? 'at ' + company : ''} regarding ${who}.
Recipient email: ${email || '(unknown)'}
Tone: professional, friendly, 120–170 words, with a 1‑line CTA and a P.S. Include a subject line.
Return only plain text, no JSON.
`.trim();

        const result = await AI.chat(prompt, { temperature: 0.4, max_tokens: 450 });
        this.$set(this.outreachDrafts, idx, (result && result.text ? result.text : '').trim());
      } catch (e) {
        this.error = 'Failed to compose outreach.';
      }
    },
    copyDraft(idx) {
      const text = this.outreachDrafts[idx];
      if (!text) return;
      try { navigator.clipboard.writeText(text); } catch (e) { void e; }
    },
    saveDraft(idx, contact) {
      const draft = this.outreachDrafts[idx];
      if (!draft) return;
      const title = `Outreach draft to ${contact.name || contact.role || 'contact'}`;
      this.ensureConv('contacts', this.query, '(outreach)', ['contacts', 'outreach']);
      ArchiveAPI.appendMessage(this.currentConv, { role: 'assistant', content: `${title}\n\n${draft}` }, ['outreach']);
    },

    // ========== SAVE / EXPORT ==========
    saveSources() {
      if (!this.collected.length) return;
      const text = [
        `🔎 Research Sources for: ${this.query}`,
        ...this.collected.map((r, i) => `${i+1}. ${r.title}\n${r.link}\n${r.snippet}`)
      ].join('\n\n');

      this.ensureConv('research', this.query, '(sources-collected)', ['research','google']);
      ArchiveAPI.appendMessage(this.currentConv, { role: 'system', content: text }, ['research','google']);
      try {
        NeuralAPI.ingest({ type: 'web_research', query: this.query, records: this.collected });
      } catch (e) { void e; }
    },
    saveContacts() {
      if (!this.contacts) return;
      this.ensureConv('contacts', this.query, JSON.stringify(this.contacts).slice(0, 400) + '…', ['contacts','synthesis']);
      ArchiveAPI.appendMessage(this.currentConv, { role: 'assistant', content: JSON.stringify(this.contacts, null, 2) }, ['contacts','synthesis']);
      try {
        NeuralAPI.ingest({ type: 'contacts_json', query: this.query, contacts: this.contacts });
      } catch (e) { void e; }
    },
    downloadJSON() {
      const blob = new Blob([JSON.stringify(this.contacts, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${(this.contacts && (this.contacts.primaryName || this.contacts.query) || this.query || 'contacts').replace(/\s+/g,'_')}.json`;
      a.click(); URL.revokeObjectURL(url);
    },
    downloadVCF() {
      if (!(this.contacts && this.contacts.contacts && this.contacts.contacts.length)) return;
      const vcfEntries = this.contacts.contacts.map(c => {
        return [
          'BEGIN:VCARD',
          'VERSION:3.0',
          `FN:${c.name || ''}`,
          `ORG:${c.company || ''}`,
          ...(c.emails || []).map(e => `EMAIL;TYPE=INTERNET:${e.email}`),
          ...(c.phones || []).map(p => `TEL;TYPE=CELL:${p.phone}`),
          'END:VCARD'
        ].join('\n');
      }).join('\n');
      const blob = new Blob([vcfEntries], { type: 'text/vcard' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${this.query.replace(/\s+/g,'_')}.vcf`;
      a.click();
      URL.revokeObjectURL(url);
    },

    // ========== GOOGLE CSE ==========
    loadGoogleCSE() {
      if (this.__cseLoaded) return;
      if (!window.__gcseLoaded) {
        const script = document.createElement('script');
        script.src = 'https://cse.google.com/cse.js?cx=87a5cd3086b3e4db7';
        script.async = true;
        script.onload = () => {
          window.__gcseLoaded = true;
          this.__cseLoaded = true;
          this.__cseReady = true;
          window.dispatchEvent(new Event('cse:ready'));
          this.$nextTick(() => this.setupResultObserver());
        };
        document.head.appendChild(script);
      } else {
        this.__cseLoaded = true;
        this.__cseReady = true;
        this.$nextTick(() => this.setupResultObserver());
      }
    },
    async triggerCseSearch(q) {
      if (!q) return false;
      if (!this.__cseReady) {
        await new Promise(r => window.addEventListener('cse:ready', r, { once: true }));
      }
      const input = await this.waitForCseInput();
      if (!input) return false;
      input.value = q;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', which: 13, keyCode: 13, bubbles: true }));
      return true;
    },
    async waitForCseInput(timeout = 4000) {
      const start = Date.now();
      while (Date.now() - start < timeout) {
        const input = document.getElementById('gsc-i-id1') || document.querySelector('input.gsc-input');
        if (input) return input;
        await this.wait(50);
      }
      return null;
    },
    setupResultObserver() {
      const container = document.getElementById('cse-results');
      if (!container || this.__resultObserver) return;
      this.__resultObserver = new MutationObserver(() => this.scrapeCseOnce());
      this.__resultObserver.observe(container, { childList: true, subtree: true });
    },
    scrapeCseOnce() {
      const container = document.getElementById('cse-results');
      if (!container) return;
      const nodes = container.querySelectorAll('.gsc-webResult.gsc-result');
      const out = [];
      nodes.forEach(n => {
        const a = n.querySelector('.gs-title a');
        const title = (a && a.textContent ? a.textContent : '').trim();
        const link = (a && a.href ? a.href : '').trim();
        const snippet = ((n.querySelector('.gs-snippet') && n.querySelector('.gs-snippet').textContent) || '').trim();
        if (title && link) out.push({ title, link, snippet });
      });
      if (out.length) this.mergeAndDedupeSources(out);
    },
    collectGoogleResults() {
      this.scrapeCseOnce();
    },

    // ========== CONTEXT (Archive + Neural) ==========
    extractKeywords(text = '', limit = 8) {
      const stop = new Set(['the','and','with','that','this','from','your','into','over','under','for','you','are','was','were','will','can','could','should','would','have','has','had','about','onto','than','then','also','such','like','when','what','how','why','where','which','who','while','to','in','on','of','a','an','is','it','i','my','me','we','our','us']);
      const words = (String(text).toLowerCase().match(/[a-z][a-z-]{2,}/g) || []).filter(w => !stop.has(w));
      const freq = {};
      for (const w of words) freq[w] = (freq[w] || 0) + 1;
      return Object.keys(freq).sort((a,b) => freq[b]-freq[a]).slice(0, limit);
    },
    buildSavedContext(userText) {
      const keys = this.extractKeywords(userText, 6);
      const keySet = new Set(keys);

      const convs = (ArchiveAPI.list() || []).slice(0, 40);
      const scored = convs
        .map(c => {
          const kws = c.keywords || [];
          const overlap = kws.filter(k => keySet.has(String(k).toLowerCase())).length;
          return { c, overlap };
        })
        .filter(x => x.overlap > 0)
        .sort((a,b) => b.overlap - a.overlap)
        .slice(0, 6)
        .map(({ c }) => {
          const last = (c.messages || []).slice(-1)[0];
          const lastLine = last ? `${last.role}: ${last.content}` : '';
          return `• ${c.title || c.mode} (${new Date(c.updatedAt || c.createdAt).toLocaleString()}): ${c.summary || lastLine}`;
        });

      let brainMatches = [];
      try {
        const q = keys.join(' ');
        brainMatches = (NeuralAPI.search(q) || [])
          .slice(0, 6)
          .map(r => {
            if (r.type === 'web_research') {
              const top = (r.records || []).slice(0,2).map(rr => `${rr.title} — ${rr.link}`).join(' | ');
              return `• research: ${r.query} ⇒ ${top}`;
            } else if (r.type === 'web_summary') {
              return `• summary: ${r.query} ⇒ ${String(r.summary).slice(0,180)}…`;
            } else if (r.type === 'image_generation') {
              const n = (r.urls || []).length;
              return `• image: ${r.prompt} [${n} url${n===1 ? '' : 's'}]`;
            }
            return `• ${r.type || 'note'}: ${JSON.stringify(r).slice(0,160)}…`;
          });
      } catch (e) { void e; }

      if (!scored.length && !brainMatches.length) return '';
      return [
        'CONTEXT FROM ARCHIVE & NEURAL BRAIN (use if relevant):',
        scored.join('\n'),
        brainMatches.join('\n')
      ].filter(Boolean).join('\n');
    },

    // ========== HELPERS ==========
    ensureConv(mode, userText, aiText, keywords = []) {
      if (!this.currentConv) {
        const created = ArchiveAPI.create({ mode, userText, aiText, keywords });
        this.currentConv = (created && created.id) ? created.id : created;
      }
      return this.currentConv;
    },
    wait(ms) { return new Promise(r => setTimeout(r, ms)); },
    tryParseJson(s = '') {
      try {
        return JSON.parse(s);
      } catch (e) {
        void e;
      }
      const m = s.match(/\{[\s\S]*\}$/);
      if (m) {
        try {
          return JSON.parse(m[0]);
        } catch (e) {
          void e;
        }
      }
      return null;
    },
    slugify(s = '') {
      return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 50) || 'id';
    }
  }
};
</script>

<style scoped>
/* ======= THEME ======= */
.contacts-app {
  --bg-primary: #0f1220;
  --bg-surface: #141834;
  --border-color: #28306a;
  --accent: #3b4cff;
  --accent-light: #7a8cff;
  --text-primary: #e8ebff;
  --text-muted: #aab2ff;
  --success: #3ddc97;
  --radius-md: 12px;
  --radius-lg: 16px;
  --shadow-base: 0 8px 32px rgba(0, 0, 0, 0.4);

  background: linear-gradient(180deg, rgba(59, 76, 255, 0.1), transparent 20%), var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  color: var(--text-primary);
  box-shadow: var(--shadow-base);
  padding: 20px 20px 30px 20px;
  width: min(1400px, 100%);
  margin: 40px auto;
  position: relative;
  overflow: hidden;
  animation: fadeIn .8s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.contacts-header h3 {
  margin: 10px 0 18px 0;
  font-size: clamp(18px, 2.6vw, 22px);
  background: linear-gradient(90deg, var(--accent), var(--accent-light));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.row { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }

input, .select, .btn {
  height: 44px;
  border-radius: var(--radius-md);
  border: 1px solid var(--border-color);
  padding: 10px 12px;
  font-size: 14px;
  color: var(--text-primary);
  background: var(--bg-surface);
  transition: all .2s ease;
}

input:focus, .select:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(59, 76, 255, 0.25);
}

.btn {
  background: linear-gradient(180deg, var(--accent), #2938c4);
  color: #fff; cursor: pointer;
  transition: transform .1s ease, box-shadow .2s ease;
}
.btn:hover { box-shadow: 0 0 12px rgba(59, 76, 255, 0.5); }
.btn:active { transform: scale(.97); }
.btn.ghost { background: transparent; border: 1px solid var(--accent-light); color: var(--text-muted); }

.muted { color: var(--text-muted); opacity: .9; }
.error { color: #ff9ea0; margin-top: 8px; }
.hint { color: #cfd5ff; margin: 8px 0 0; }

/* SOURCE TOOLS */
.source-tools { margin-top: 14px; }
.google-cse-wrapper {
  margin-top: 8px;
  border: 1px dashed var(--border-color);
  border-radius: var(--radius-md);
  padding: 10px;
  background: linear-gradient(180deg, rgba(18, 23, 54, .65), rgba(18, 23, 54, .35));
}

.results ul {
  list-style: none;
  padding: 0; margin: 10px 0 0;
}
.results li {
  padding: 10px 0;
  border-bottom: 1px dashed #27306b;
}
.results li:last-child { border-bottom: none; }
.results .snippet { color: #cfd5ff; opacity: 0.9; }
.results a { color: var(--text-primary); text-decoration: none; }
.results a:hover { text-decoration: underline; }

/* SYNTH */
.synth { margin-top: 16px; }
.synth-head {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px; justify-content: space-between;
  border-bottom: 1px solid #202a5c; padding-bottom: 8px;
}
.synth-head h4 { margin: 0; font-size: clamp(16px, 2.4vw, 20px); }
.summary { color: #cfd5ff; margin-top: 6px; }

.profiles ul, .suggestions ul { list-style: none; margin: 8px 0 0; padding: 0; }
.profiles li, .suggestions li { padding: 6px 0; }

/* FILTERS */
.filter-bar { margin: 14px 0 4px; }
.chipbar { display: flex; gap: 8px; margin-top: 8px; }
.chip { border: 1px solid var(--border-color); background: #181c3a; color: var(--text-primary); border-radius: 999px; padding: 6px 10px; cursor: pointer; }
.chip.active { border-color: var(--accent); color: #fff; }

/* GRID */
.contacts-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px; margin-top: 16px;
}
.card {
  border: 1px solid var(--border-color);
  background: linear-gradient(180deg, rgba(18, 23, 54, 0.85), rgba(18, 23, 54, 0.6));
  border-radius: var(--radius-md); padding: 14px;
  transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
  position: relative; overflow: hidden;
}
.card:hover { transform: translateY(-4px); border-color: var(--accent); box-shadow: 0 8px 24px rgba(59, 76, 255, 0.2); }
.card header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.card .conf { font-size: 12px; color: var(--text-muted); }
.card::before {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(circle at top left, rgba(59, 76, 255, 0.12), transparent 70%);
  pointer-events: none; opacity: 0; transition: opacity .4s ease;
}
.card:hover::before { opacity: 1; }
.verified { border-color: var(--success); background: rgba(61, 220, 151, 0.08); }
.badge.verified { background: var(--success); color: #fff; padding: 2px 6px; font-size: 11px; border-radius: 4px; }
.label { color: var(--accent-light); margin-top: 6px; font-size: 13px; font-weight: bold; }
.tiny { margin: 4px 0 0 0; padding-left: 16px; }
.sources a { margin-right: 8px; font-size: 12px; color: #cfd5ff; }
.ai-suggestion { margin-top: 8px; font-style: italic; color: #9aa3ff; }

/* OUTREACH */
.outreach { margin-top: 10px; }
.outreach-draft { margin-top: 8px; }
.outreach-draft textarea {
  width: 100%; min-height: 120px; padding: 10px; border-radius: 8px; border: 1px solid var(--border-color);
  background: #0f1435; color: #e8ebff; resize: vertical;
}

/* TRANSITIONS */
.fade-slide-enter-active, .fade-slide-leave-active { transition: all .35s ease; }
.fade-slide-enter, .fade-slide-leave-to { opacity: 0; transform: translateY(12px); }

/* LIGHT MODE */
@media (prefers-color-scheme: light) {
  .contacts-app {
    --bg-primary: #f7f8ff; --bg-surface: #fff; --text-primary: #0f153a; --border-color: #ccd2ff;
    background: linear-gradient(180deg, rgba(59, 76, 255, 0.06), transparent 20%), var(--bg-primary);
  }
  .chip { background: #f2f4ff; color: #1d2250; }
}
.ai-report {
  background: #1a1f2b;
  border: 1px solid #444;
  padding: 1rem;
  margin-top: 1.5rem;
  border-radius: 8px;
  color: #e0e0e0;
}

.report-block {
  white-space: pre-wrap;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  background: #111;
  padding: 0.75rem;
  border-radius: 4px;
  margin-top: 0.5rem;
}

</style>
